import { useMutation, useQuery } from '@apollo/client';
import { css } from '@emotion/core';
import { IconButton, Tooltip } from '@material-ui/core';
import { Cancel } from '@material-ui/icons';
import { capitalize } from 'lodash';
import React from 'react';

import { Duration } from '../common/duration';
import { PageDialogLink } from '../common/page-dialog-link';
import { fullDateTime } from '../common/timestamp';
import { JobCandidatesBoard } from '../containers/job-candidates-board';
import { SCHEDULED_OUTREACHES, ScheduledOutreach, UPDATE_SCHEDULED_OUTREACHES } from '../graphql/queries/outreach';
import { useModal } from '../hooks/use-modal';
import { useSession } from '../hooks/use-session';
import { useSnackbar } from '../hooks/use-snackbar';
import { CellValue, DataPanel, DataPanelColumn } from './data-panel';

const statusStyles = css`
    display: flex;
    align-items: center;

    .cancel-outreach-button {
        margin-left: 8px;
        opacity: 0;
        transition: opacity 0.2s ease-in-out;
    }

    &:hover .cancel-outreach-button {
        opacity: 1;
    }
`;

type Column = 'recruiter' | 'sendingTime' | 'requestedTime' | 'status' | 'requested' | 'added' | 'client' | 'job';

const columns: Array<DataPanelColumn<Column>> = [
    { id: 'client', label: 'Client' },
    { id: 'job', label: 'Job' },
    { id: 'recruiter', label: 'Recruiter' },
    { id: 'requestedTime', label: 'Requested Time' },
    { id: 'status', label: 'Status' },
    { id: 'requested', label: 'Requested' },
    { id: 'sendingTime', label: 'Sending Time' },
    { id: 'added', label: 'Added' }
];

export const OutreachesDataPanel: React.FC<{
    duration: Duration;
    username?: string;
    jobId?: string;
    refetchTrigger?: number;
}> = ({ username, jobId, duration, refetchTrigger }) => {
    const { user } = useSession();
    const { getConfirmation } = useModal();
    const { setSnackbar } = useSnackbar();
    const [updateScheduledOutreach, { loading: updateLoading }] = useMutation<
        {},
        { id: string; updates: Partial<ScheduledOutreach> }
    >(UPDATE_SCHEDULED_OUTREACHES);

    const buildWhereClause = () => {
        const dateFilter = {
            _or: [
                { requestedAt: { _gte: duration.start, _lte: duration.end } },
                { scheduledAt: { _gte: duration.start, _lte: duration.end } }
            ]
        };

        const filters: any[] = [dateFilter];

        const userId = username === 'me' ? user.id : undefined;
        if (userId) {
            filters.push({ userId: { _eq: userId } });
        }

        if (jobId) {
            filters.push({ jobId: { _eq: jobId } });
        }

        return filters.length > 1 ? { _and: filters } : dateFilter;
    };

    const {
        data: outreachesData,
        loading: fetchLoading,
        refetch
    } = useQuery<{ outreaches: ScheduledOutreach[] }, { where: any }>(SCHEDULED_OUTREACHES, {
        variables: {
            where: buildWhereClause()
        }
    });

    React.useEffect(() => {
        if (refetchTrigger && refetchTrigger > 0) {
            refetch();
        }
    }, [refetchTrigger, refetch]);

    const handleCancelOutreach = (id: string) => () => {
        getConfirmation(
            async () => {
                await updateScheduledOutreach({
                    variables: {
                        id,
                        updates: {
                            status: 'canceled'
                        }
                    }
                });
                setSnackbar('Scheduled Outreach Canceled');
                refetch();
            },
            'This will cancel this scheduled outreach',
            'Please confirm'
        );
    };

    const getCellValue = (column: Column) => (record: ScheduledOutreach) => {
        switch (column) {
            case 'recruiter':
                return record.user.name;
            case 'sendingTime':
                return record.scheduledAt;
            case 'requestedTime':
                return record.requestedAt;
            case 'status':
                return capitalize(record.status);
            case 'requested':
                return record.limit;
            case 'added':
                return record.addedCount ?? 0;
            case 'client':
                return record.job.client.name;
            case 'job':
                return record.job.title;
            default:
                return null;
        }
    };

    const getCellRenderer = {
        job: (_1: CellValue, row: ScheduledOutreach) => {
            return (
                <PageDialogLink
                    url={`/job/${row.job.id}/board`}
                    Component={JobCandidatesBoard}
                    componentProps={{ jobId: row.job.id }}
                >
                    <span>{row.job.title}</span>
                </PageDialogLink>
            );
        },
        requestedTime: fullDateTime,
        sendingTime: fullDateTime,
        status: (_1: CellValue, row: ScheduledOutreach) => {
            const cancelButton =
                row.status === 'scheduled' && row.user.id === user.id ? (
                    <Tooltip title="Cancel Outreach">
                        <span className="cancel-outreach-button">
                            <IconButton
                                disabled={updateLoading || fetchLoading}
                                onClick={handleCancelOutreach(row.id)}
                                size="small"
                            >
                                <Cancel fontSize="small" />
                            </IconButton>
                        </span>
                    </Tooltip>
                ) : null;
            return (
                <div css={statusStyles}>
                    {capitalize(row.status)}
                    {cancelButton}
                </div>
            );
        }
    };

    const displayedColumns = jobId
        ? columns.filter((c) => c.id !== 'job' && c.id !== 'client')
        : username
          ? columns.filter((c) => c.id !== 'recruiter')
          : columns;

    const renderSummary = (data: ScheduledOutreach[]) => {
        const totalRequested = data
            .filter((o) => o.status === 'scheduled' || o.status === 'complete')
            .reduce((total, o) => total + o.limit, 0);
        const totalAdded = data.reduce((total, o) => total + (o.addedCount ?? 0), 0);
        return (
            <div>
                Total Added: {totalAdded} / Total Requested: {totalRequested}
            </div>
        );
    };

    return (
        <DataPanel
            columns={displayedColumns}
            data={outreachesData?.outreaches}
            getCellValue={getCellValue}
            filterableColumns={['client', 'recruiter', 'status']}
            pageSize={100}
            getCellRenderer={getCellRenderer}
            sortableColumns={['sendingTime', 'requestedTime', 'client', 'recruiter', 'added', 'requested', 'status']}
            initialSortColumn="requestedTime"
            initialSortDirection="desc"
            renderSummary={renderSummary}
        />
    );
};
